import { Icon } from '@chakra-ui/react';
import { FC, useEffect, useRef, useState } from 'react';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { SESSION_ID_DISPLAY_LENGTH } from '../../constants/chat';
import { useNavigatorPermission } from '../../hooks/useNavigatorPermission';
import { useChatSocket } from '../../services/chatSocket';
import { copyToClipboard } from '../../utils';
import { MButton, MText, MTooltip } from '../Monetize';

export const ChatSessionId: FC = () => {
  const { sessionId } = useChatSocket();
  const { canCopyClipboard } = useNavigatorPermission();
  const [isCopied, setIsCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  if (!sessionId) {
    return null;
  }

  const shortSessionId = sessionId.slice(0, SESSION_ID_DISPLAY_LENGTH);

  if (!canCopyClipboard) {
    return (
      <MText
        fontSize="xxs"
        fontFamily="mono"
        color="fg.subtle"
        title={sessionId}
      >
        {shortSessionId}
      </MText>
    );
  }

  const handleCopy = async () => {
    const didCopy = await copyToClipboard(sessionId);
    if (!didCopy) {
      return;
    }
    setIsCopied(true);
    clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <MTooltip
      label={isCopied ? 'Copied' : 'Copy session ID'}
      placement="bottom"
      hasArrow
      closeOnClick={false}
    >
      <MButton
        data-testid="chat-session-id-btn"
        aria-label="Copy session ID"
        size="xs"
        variant="tertiary"
        color="fg.subtle"
        fontSize="xxs"
        fontFamily="mono"
        fontWeight={500}
        h="auto"
        minW={0}
        px={1}
        py={0.5}
        rightIcon={
          <Icon as={isCopied ? MdCheck : MdContentCopy} boxSize="10px" />
        }
        onClick={handleCopy}
      >
        {shortSessionId}
      </MButton>
    </MTooltip>
  );
};
